import { XIcon } from '@phosphor-icons/react';
import { forwardRef, useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';

import SearchIcon from '@/assets/svgs/Search';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

type SearchFieldProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>, 'onChange'
> & {
  onChange: (value: string) => void;
};

export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  ({ className, type, defaultValue, ...props }, ref) => {
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebounce(search, 600);

    useEffect(() => {
      if (debouncedSearch) {
        props.onChange?.(debouncedSearch);
      }
    }, [debouncedSearch]);

    useEffect(() => {
      setSearch(String(defaultValue));
    }, []);

    const handleClearSearch = () => {
      setSearch('');
      props.onChange?.('');
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
      }
      props.onKeyDown?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      if (value.length > 0) {
        setSearch(value);
      } else {
        setSearch('');
        props.onChange?.('');
      }
    };

    return (
      <div className={cn('relative', className)}>
        <div className="absolute left-[10px] top-[6px]">
          <SearchIcon />
        </div>
        <Input
          ref={ref}
          data-testid="search-field"
          name="search"
          className="pl-10 pr-4 h-[37px]"
          value={search}
          {...props}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        {search && (
          <div
            className="absolute right-[10px] top-[6px] cursor-pointer"
            onClick={handleClearSearch}
          >
            <XIcon size={24} />
          </div>
        )}
      </div>
    );
  }
);

SearchField.displayName = 'SearchField';
